import React, { useState } from 'react';
import Button from '../../components/Button';
import Empty from '../../components/Empty';
import FontAwesomeIcon from '../../components/Icon';
import SpecificationForm from '../../components/SpecificationForm/SpecificationForm';
import SpecificationsRepeater from 'src/components/SpecificationsRepeater/SpecificationsRepeater';
import SpecificationsSkeletonList from 'src/pages/Item/skeletons/SpecificationsSkeletonList';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import { onCheckUserCanAccessTheModule } from 'src/helpers/validationHelper';
import { USER_ROLES } from 'src/common/constants';
import Search from 'src/components/Search/Search';
import {
  Specification,
  SpecificationSavePayload,
  UseSpecificationsReturn,
} from 'src/SharedCustomHooks/UseSpecifications/useSpecifications';
import './ItemSpecifications.scss';

interface ItemSpecificationsProps {
  item: { id: string; name: string };
  specificationsAPI: UseSpecificationsReturn;
}

const ItemSpecifications = ({
  item,
  specificationsAPI,
}: ItemSpecificationsProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [specification, setSpecification] = useState<Partial<Specification>>(
    {}
  );

  const {
    specifications,
    loading,
    isSaving,
    searchTerm,
    setSearchTerm,
    saveSpecification,
    updateSpecification,
    deleteSpecification,
  }: UseSpecificationsReturn = specificationsAPI;

  const isEnabled: boolean = onCheckUserCanAccessTheModule([
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.ADMIN,
    USER_ROLES.PROPERTY_DEVELOPER,
  ]);

  const onClose = (): void => {
    setShowOverlay(false);
    setSpecification({});
  };

  const onSaveOrUpdate = async (
    params: SpecificationSavePayload
  ): Promise<boolean> => {
    if (params.id) {
      return await updateSpecification(params);
    }

    return await saveSpecification(params);
  };

  const editSpecification = (data: Specification): void => {
    setSpecification(data);
    setShowOverlay(true);
  };

  const onDeleteSpecification = (data: Specification): void => {
    void deleteSpecification(data.id);
  };

  return (
    <div className="item-specifications">
      <div className="item-specifications-header">
        <div className="item-specifications-search">
          <Search
            size="small"
            placeholder="Search Specifications..."
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value.trim())}
            theme={undefined}
          />
        </div>
        {isEnabled && (
          <Button
            color="teal"
            label="Add Specification"
            onClick={() => setShowOverlay(true)}
            icon={<FontAwesomeIcon glyph="edit" weight="light" size="small" />}
            size="small"
          />
        )}
      </div>
      {loading ? (
        <SpecificationsSkeletonList />
      ) : specifications.length > 0 ? (
        <SpecificationsRepeater
          specifications={specifications}
          canEdit={isEnabled}
          onEdit={editSpecification}
          onDelete={onDeleteSpecification}
        />
      ) : (
        <Empty
          headline="No Specifications yet"
          cta={
            isEnabled && (
              <Button
                color="teal"
                label="Add Specification"
                onClick={() => setShowOverlay(true)}
                icon={
                  <FontAwesomeIcon glyph="edit" weight="light" size="small" />
                }
                size="small"
              />
            )
          }
          image={
            <img alt="No Specifications yet" src="/assets/empty/notes.svg" />
          }
          strapline={''}
        />
      )}
      <GenericDialogue
        isOpen={showOverlay}
        onClose={onClose}
        title={specification?.id ? 'Edit Specification' : 'Add Specification'}
        description={
          specification?.id
            ? `Update this specification for ${item.name}.`
            : `Add a new specification to ${item.name}.`
        }
        maxWidth="80rem"
      >
        <SpecificationForm
          itemId={item.id}
          specification={specification}
          saving={isSaving}
          onClose={onClose}
          onSaveSpecification={onSaveOrUpdate}
        />
      </GenericDialogue>
    </div>
  );
};

export default ItemSpecifications;
